import { Stack } from '@mui/material';
import {
  FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS,
  FlowAuthorizationStatusDetailsRequirement,
  type FlowHumanTaskBlockConfig,
  FlowHumanTaskBlockWorkContextContentType,
  FlowHumanTaskPresentationMode,
  FlowHumanTaskTool,
  type FlowHumanTaskToolClinicalQuestionAssistantConfig,
  FlowHumanTaskType,
  type FlowPriorAuthorizationInformation,
} from 'flow-schema';
import { startCase } from 'lodash';

import { HumanTaskDynamicConfigTabs } from './DynamicConfig/Tabs';
import { HumanTaskStandardTabs } from './StandardTabs';
import {
  getCustomClinicalQuestionAssistantConfig,
  getPriorAuthorizationInformationWithClinicalQuestionAssistantConfig,
} from './ToolsTab/ClinicalQuestionAssistant/configMapping';
import { getDefaultHumanTaskWorkContextConfig } from './WorkContexts/Fields/constants';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

import HumanTaskTool = FlowHumanTaskTool;
type HumanTaskWorkContexts = FlowHumanTaskBlockConfig['workContexts'];

const TASK_DEFAULT_PRESENTATION_MODES: Record<FlowHumanTaskType, FlowHumanTaskPresentationMode> = {
  [FlowHumanTaskType.SubmitPriorAuthorization]: FlowHumanTaskPresentationMode.BrowserExtension,
  [FlowHumanTaskType.DynamicConfig]: FlowHumanTaskPresentationMode.Standard,
  [FlowHumanTaskType.CustomSubtask]: FlowHumanTaskPresentationMode.Standard,
};

export function HumanTaskFormFields() {
  const { setValue: setValuePresentationMode, value: presentationMode } =
    useSelectedNodeField<FlowHumanTaskPresentationMode>('config.presentationMode');
  const { value: workContexts = [], setValue: setWorkContexts } =
    useSelectedNodeField<HumanTaskWorkContexts>('config.workContexts');
  const { value: taskType } = useSelectedNodeField<FlowHumanTaskType>('config.taskType');
  const { value: enabledTools, setValue: setEnabledTools } = useSelectedNodeField<HumanTaskTool[]>(
    'config.tools.enabledTools'
  );
  const { value: clinicalQuestionAssistantConfig, setValue: setClinicalQuestionAssistantConfig } =
    useSelectedNodeField<FlowHumanTaskToolClinicalQuestionAssistantConfig>(
      'config.tools.config.clinicalQuestionAssistant'
    );
  const { value: priorAuthorizationInformation, setValue: setPriorAuthorizationInformation } =
    useSelectedNodeField<FlowPriorAuthorizationInformation>('config.priorAuthorizationInformation');
  const { setValue: setAuthorizationStatusDetailsRequirement } =
    useSelectedNodeField<FlowAuthorizationStatusDetailsRequirement>(
      'config.authorizationStatusDetailsRequirement'
    );

  return (
    <Stack gap={1}>
      <BlockSelectField
        fieldName="config.taskType"
        label="Task Type"
        required
        options={Object.values(FlowHumanTaskType).map((value) => ({
          label: value,
          value,
        }))}
        onChange={(event) => {
          const nextTaskType = event.target.value as FlowHumanTaskBlockConfig['taskType'];
          if (taskType !== nextTaskType) {
            if (
              taskType === FlowHumanTaskType.SubmitPriorAuthorization &&
              nextTaskType === FlowHumanTaskType.CustomSubtask
            ) {
              setClinicalQuestionAssistantConfig(
                getCustomClinicalQuestionAssistantConfig({
                  existingConfig: clinicalQuestionAssistantConfig,
                  priorAuthorizationInformation,
                })
              );
            }
            if (
              taskType === FlowHumanTaskType.CustomSubtask &&
              nextTaskType === FlowHumanTaskType.SubmitPriorAuthorization
            ) {
              setPriorAuthorizationInformation(
                getPriorAuthorizationInformationWithClinicalQuestionAssistantConfig({
                  clinicalQuestionAssistantConfig,
                  priorAuthorizationInformation,
                })
              );
            }
            setEnabledTools(
              (enabledTools ?? FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS).filter(
                (enabledTool) => enabledTool !== HumanTaskTool.ClinicalQuestionAssistant
              )
            );
          }

          const defaultPresentationMode = TASK_DEFAULT_PRESENTATION_MODES[nextTaskType];
          setValuePresentationMode(defaultPresentationMode);
          if (nextTaskType === FlowHumanTaskType.SubmitPriorAuthorization) {
            const existingPortalWorkContext = workContexts.find((workContext) => {
              return workContext.contentType === FlowHumanTaskBlockWorkContextContentType.Portal;
            });

            setWorkContexts([
              existingPortalWorkContext ??
                getDefaultHumanTaskWorkContextConfig(
                  FlowHumanTaskBlockWorkContextContentType.Portal
                ),
            ]);
            setAuthorizationStatusDetailsRequirement(
              FlowAuthorizationStatusDetailsRequirement.Required
            );
          }
        }}
      />

      <BlockSelectField
        options={Object.values(FlowHumanTaskPresentationMode).map((value) => ({
          label:
            value === FlowHumanTaskPresentationMode.BrowserExtension
              ? `Sidekick (${startCase(value)})`
              : startCase(value),
          value,
        }))}
        label="Presentation Mode"
        fieldName="config.presentationMode"
        defaultValue={taskType && TASK_DEFAULT_PRESENTATION_MODES[taskType]}
        helperText={
          presentationMode === FlowHumanTaskPresentationMode.BrowserExtension
            ? 'Task opens in the Sidekick Chrome extension sidebar.'
            : 'Task opens in the Notable platform.'
        }
      />

      {taskType === FlowHumanTaskType.DynamicConfig ? (
        <HumanTaskDynamicConfigTabs />
      ) : (
        <HumanTaskStandardTabs taskType={taskType} />
      )}
    </Stack>
  );
}
